import { useCallback } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { useGetExperimentVariant } from "@hopper-b2b/experiments";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import { writeInitialTripsFilter } from "@hopper-b2b/trips";
import { type FlightSummaryInfo, MyTripsFilter } from "@hopper-b2b/types";
import { EXPERIMENTS } from "../../../../App";
import BestPriceIcon from "../../../../assets/client/best-price.svg?react";
import BookingConfirmed from "../../../../assets/client/booking-confirmed.svg";
import Logo from "../../../../assets/client/client-logo-white.svg?react";
import Coin from "../../../../assets/client/coin-rotated.svg?react";
import InfoIcon from "../../../../assets/client/outlined-warn-circle.svg?react";
import { RedirectLoader } from "../../../../components/Body/RedirectLoader";
import { useEnhancedEarnAmount } from "../../../../hooks/shared";
import { useCheckoutMyTripsNavigation } from "../../../../hooks/useCheckoutMyTripsNavigation";
import {
  PATH_HOTELS_SEARCH,
  PATH_TRIPS_FLIGHTS,
} from "../../../../utils/urlPaths";
import "./styles.scss";

export interface ICrossSellConfirmationProps {
  outgoingInfo: FlightSummaryInfo;
}

export const CrossSellConfirmation = ({
  outgoingInfo,
}: ICrossSellConfirmationProps) => {
  const { t } = useI18nContext();
  const navigate = useNavigate();
  const maxInstallments = useGetExperimentVariant(EXPERIMENTS.MAX_INSTALLMENTS);
  const { cashbackPercent } = useEnhancedEarnAmount();

  const redirectToLegacyTrips = useCallback(() => {
    writeInitialTripsFilter(MyTripsFilter.FLIGHTS);
    navigate(PATH_TRIPS_FLIGHTS);
  }, [navigate]);
  const { navigateToMyTrips: redirectToTrips, nativeHandoffPending } =
    useCheckoutMyTripsNavigation(redirectToLegacyTrips);

  const redirectToHotelSearch = useCallback(() => {
    navigate(PATH_HOTELS_SEARCH);
  }, [navigate]);

  const destinationCity = outgoingInfo.destinationCity;

  return (
    <div className="flight-confirmation-root mobile cross-sell">
      {nativeHandoffPending ? <RedirectLoader /> : null}
      <div className="flight-confirmation-header">
        <img
          className="flight-confirmation-header-image"
          src={BookingConfirmed}
          alt=""
        />
        <h2 className="flight-confirmation-title">
          {t("bookingConfirmation.crossSell.nextDestination", {
            city: destinationCity,
          })}
        </h2>
        <div className="flight-confirmation-subtitle">
          <p className="flight-confirmation-subtitle-main">
            <Trans
              i18nKey="bookingConfirmation.crossSell.ticketingWarning"
              components={{ bold: <span style={{ fontWeight: 500 }} /> }}
            />
          </p>
          <p className="flight-confirmation-subtitle-notice">
            {t("bookingConfirmation.crossSell.ticketingRefundNotice")}
          </p>
        </div>
      </div>

      <div className="flight-confirmation-info-section">
        <div className="flight-confirmation-info-section-title">
          {t("bookingConfirmation.crossSell.importantInfo")}
        </div>

        <div className="flight-confirmation-info-row">
          <div className="flight-confirmation-info-row-icon">
            <BestPriceIcon />
          </div>
          <div className="flight-confirmation-info-row-content">
            <div className="flight-confirmation-info-row-title">
              {t("bookingConfirmation.crossSell.bestPriceTitle")}
            </div>
            <div className="flight-confirmation-info-row-description">
              {t("bookingConfirmation.crossSell.bestPriceDescription")}
            </div>
          </div>
        </div>

        <div className="flight-confirmation-info-row-divider" />

        <div className="flight-confirmation-info-row">
          <div className="flight-confirmation-info-row-icon">
            <InfoIcon />
          </div>
          <div className="flight-confirmation-info-row-content">
            <div className="flight-confirmation-info-row-title">
              {t("bookingConfirmation.crossSell.freeCancellationTitle")}
            </div>
            <div className="flight-confirmation-info-row-description">
              {t("bookingConfirmation.crossSell.freeCancellationDescription")}
            </div>
          </div>
        </div>

        <div className="flight-confirmation-info-row-divider" />

        <div className="flight-confirmation-info-section-title">
          {t("bookingConfirmation.crossSell.moreForYourTrip")}
        </div>

        <button
          onClick={redirectToHotelSearch}
          className="flight-confirmation-hotels-content"
        >
          <div className="flight-confirmation-hotels-header">
            <div className="flight-confirmation-hotels-subtitle">
              {t("bookingConfirmation.crossSell.complete")}
            </div>
            <div className="flight-confirmation-hotels-title">
              {t("bookingConfirmation.crossSell.hotelsIn", {
                city: destinationCity,
              })}
            </div>
            <div className="flight-confirmation-hotels-tags">
              <div className="badge cashback-badge">
                {t("bookingConfirmation.crossSell.cashback", {
                  percentage: cashbackPercent,
                })}
              </div>
              <div className="badge installments-badge">
                {t("upToInterestFreeInstallmentsShort", {
                  count: parseInt(maxInstallments, 10),
                })}
              </div>
            </div>
          </div>
          <div className="flight-confirmation-hotels-uv">
            <div className="uv-content">
              <div className="logo">
                <Logo /> {t("ultravioleta")}
              </div>
              <div className="protection">
                {t("bookingConfirmation.crossSell.bestPriceGuarantee")}
              </div>
            </div>
            <div className="uv-icon-container">
              <Coin className="uv-icon" />
            </div>
          </div>
        </button>
      </div>

      <div className="flight-confirmation-bottom-bar">
        <button
          className="flight-confirmation-track-button"
          onClick={redirectToTrips}
        >
          {t("bookingConfirmation.crossSell.trackBooking")}
        </button>
      </div>
    </div>
  );
};
